import type { InboxAgentNotification } from "@amoreg/contracts";
import { XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useOrganizationFindings,
  useRecommendations,
  type Recommendation,
  type RecommendationsView,
  type PolicyFinding,
} from "../../state/governance";
import { useOrgProfile } from "../../state/orgProfile";
import { RejectDialog } from "../regulation/policy/RejectDialog";
import { isOpenSuggestion } from "../regulation/policy/suggestionsView";
import { Button } from "../ui/button";
import {
  notificationLinkedIds,
  type LinkedRecordAvailability,
} from "./inboxDetailActions";
import {
  findingRecordState,
  LinkedRecordHeading,
  LinkedRecordRow,
  LinkedRecordSkeleton,
  suggestionRecordState,
} from "./LinkedRecordRow";

/**
 * Which of an update's linked records the acting organization can still
 * resolve. Shared by the body and the footer so the two cannot disagree about
 * whether a record is there: an organization it links nothing of is never
 * fetched (an empty id short-circuits the governance atom), and a lookup that
 * is pending or unavailable reports `null` rather than "gone".
 */
export function useNotificationLinkedRecords(
  item: InboxAgentNotification,
): LinkedRecordAvailability {
  const linksFinding = notificationLinkedIds(item, "finding").length > 0;
  const linksSuggestion = notificationLinkedIds(item, "suggestion").length > 0;
  const findings = useOrganizationFindings(linksFinding ? item.organizationId : null);
  const suggestions = useRecommendations(linksSuggestion ? item.organizationId : null);
  const resolvedFindings =
    linksFinding && !findings.isPending && !findings.unavailableReason
      ? (findings.snapshot?.findings ?? null)
      : null;
  const resolvedSuggestions =
    linksSuggestion && !suggestions.isPending && !suggestions.unavailableReason
      ? (suggestions.snapshot?.recommendations ?? null)
      : null;
  return useMemo(
    () => ({
      findings: resolvedFindings ? new Set(resolvedFindings.map((finding) => finding.id)) : null,
      suggestions: resolvedSuggestions
        ? new Set(resolvedSuggestions.map((suggestion) => suggestion.id))
        : null,
    }),
    [resolvedFindings, resolvedSuggestions],
  );
}

/** A notification is content, not executable UI. All controls and destinations are app-owned. */
export function NotificationBody({
  item,
  available = null,
}: {
  readonly item: InboxAgentNotification;
  readonly available?: LinkedRecordAvailability | null;
}) {
  return (
    <>
      <h2 className="text-xl font-semibold tracking-tight">{item.title}</h2>
      {item.summary && (
        <aside aria-label="Summary" className="my-6 rounded-lg bg-muted/50 px-4 py-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            TL;DR
          </p>
          <p className="text-sm leading-6">{item.summary}</p>
        </aside>
      )}
      <div className="inbox-message mt-6 break-words text-sm leading-7">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          skipHtml
          disallowedElements={["img", "input"]}
          components={{
            a: ({ children, href }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4"
              >
                {children}
              </a>
            ),
          }}
        >
          {item.body}
        </ReactMarkdown>
      </div>
      {item.actions?.some((action) => action.kind === "finding") && (
        <NotificationFindings item={item} available={available} />
      )}
      {item.actions?.some((action) => action.kind === "suggestion") && (
        <NotificationSuggestions item={item} available={available} />
      )}
      {/* Monitoring carries no linked record to describe, so it is a footer
          action only — a lone button trailing the prose was the "wall of text
          that ends in a button" this redesign removes. */}
    </>
  );
}
function NotificationFindings({
  item,
  available,
}: {
  readonly item: InboxAgentNotification;
  readonly available: LinkedRecordAvailability | null;
}) {
  const { snapshot, isPending, unavailableReason, refresh } = useOrganizationFindings(
    item.organizationId,
  );
  const ids = notificationLinkedIds(item, "finding");
  return (
    <section aria-label="Linked findings" className="mt-8">
      <LinkedRecordHeading label="Findings" count={ids.length} />
      {isPending ? (
        <LinkedRecordSkeleton rows={ids.length} />
      ) : unavailableReason ? (
        <Button size="xs" variant="secondary" onClick={refresh}>
          Retry loading findings
        </Button>
      ) : (
        ids.map((id) => {
          const finding = snapshot?.findings.find((finding) => finding.id === id);
          return finding ? (
            <FindingActions key={id} finding={finding} item={item} />
          ) : (
            <p key={id} className="py-2 text-xs text-muted-foreground">
              This finding is no longer available.
            </p>
          );
        })
      )}
    </section>
  );
}
export function FindingActions({
  finding,
  item,
}: {
  finding: PolicyFinding;
  item: InboxAgentNotification;
}) {
  // A finding is read here and decided on its own page: the row carries no
  // control of its own.
  const state = findingRecordState(finding.status);
  const org = item.organizationSlug || item.organizationId;
  return (
    <LinkedRecordRow
      state={state}
      title={finding.title}
      summary={finding.summary}
      to={{ to: "/$org/compliance/findings/$findingId", params: { org, findingId: finding.id } }}
      openLabel="Open finding"
    />
  );
}

function NotificationSuggestions({
  item,
  available,
}: {
  readonly item: InboxAgentNotification;
  readonly available: LinkedRecordAvailability | null;
}) {
  const { snapshot, isPending, unavailableReason, refresh, decide } = useRecommendations(
    item.organizationId,
  );
  const { canManage } = useOrgProfile(item.organizationId);
  const ids = notificationLinkedIds(item, "suggestion");
  return (
    <section aria-label="Linked suggestions" className="mt-8">
      <LinkedRecordHeading label="Suggestions" count={ids.length} />
      {isPending ? (
        <LinkedRecordSkeleton rows={ids.length} />
      ) : unavailableReason ? (
        <Button size="xs" variant="secondary" onClick={refresh}>
          Retry loading suggestions
        </Button>
      ) : (
        ids.map((id) => {
          const suggestion = snapshot?.recommendations.find((candidate) => candidate.id === id);
          return suggestion ? (
            <SuggestionActions
              key={id}
              suggestion={suggestion}
              item={item}
              available={available}
              canManage={canManage}
              decide={decide}
            />
          ) : (
            <p key={id} className="py-2 text-xs text-muted-foreground">
              This suggestion is no longer available.
            </p>
          );
        })
      )}
    </section>
  );
}
function SuggestionActions({
  suggestion,
  item,
  available = null,
  canManage,
  decide,
}: {
  suggestion: Recommendation;
  item: InboxAgentNotification;
  available?: LinkedRecordAvailability | null;
  canManage: boolean;
  decide: RecommendationsView["decide"];
}) {
  const [rejecting, setRejecting] = useState(false);
  const [rejected, setRejected] = useState(false);
  const actionable = isOpenSuggestion(suggestion) && !rejected;
  const state = rejected ? "rejected" : suggestionRecordState(suggestion.status);
  const org = item.organizationSlug || item.organizationId;
  return (
    <>
      <LinkedRecordRow
        state={state}
        title={suggestion.title}
        summary={suggestion.rationale}
        to={{
          to: "/$org/compliance/suggestions/$recommendationId",
          params: { org, recommendationId: suggestion.id },
        }}
        openLabel="Open suggestion"
        trailing={
          canManage && actionable ? (
            <Button size="xs" variant="ghost" className="text-muted-foreground" onClick={() => setRejecting(true)}>
              <XIcon />
              Reject
            </Button>
          ) : null
        }
      />
      <RejectDialog
        open={rejecting}
        onOpenChange={setRejecting}
        what="suggestion"
        title={suggestion.title}
        onReject={async ({ reason, proof }) => {
          const error = await decide(suggestion.id, "dismiss", {
            note: reason,
            ...(proof ? { proof } : {}),
          });
          if (!error) setRejected(true);
          return error;
        }}
      />
    </>
  );
}
